import React from 'react';

export const hmacGeneratorContent = {
  title: 'HMAC Generator',
  description: 'Generate HMAC (Hash-based Message Authentication Code) with our free online tool. Create secure authentication codes for API security.',

  content: (
    <div className="space-y-8">
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-4">What is HMAC Generator?</h3>
        <p className="text-gray-700 leading-relaxed mb-4">
          An HMAC Generator is a cryptographic tool that creates Hash-based Message Authentication Codes using a secret key and hash function to verify both data integrity and authenticity. HMAC combines a cryptographic hash function (like SHA-256) with a secret key to produce a fixed-length authentication code that can only be verified by parties possessing the same secret key. This mechanism ensures that data has not been tampered with and originates from a trusted source, making it essential for API security, message authentication, and digital signatures.
        </p>
        <p className="text-gray-700 leading-relaxed">
          The generator implements the HMAC algorithm as defined in RFC 2104, supporting various hash functions including SHA-256, SHA-1, MD5, and SHA-512. The tool processes input messages and secret keys through the HMAC algorithm, which involves inner and outer hash computations with key padding. This design ensures that even if the underlying hash function has vulnerabilities, the HMAC construction remains secure against length extension attacks and other cryptographic threats.
        </p>
      </div>

      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-4">Why HMAC Generation is Important?</h3>
        <p className="text-gray-700 leading-relaxed mb-4">
          HMAC generation is fundamental to modern API security and authentication systems. In web services and microservices architecture, HMAC provides a robust method for authenticating API requests without transmitting passwords over the network. By including an HMAC signature with each request, services can verify that requests originate from authorized clients and haven't been modified in transit. This approach is widely used in REST APIs, webhook security, and cloud service authentication.
        </p>
        <p className="text-gray-700 leading-relaxed mb-4">
          For data integrity and message authentication, HMAC ensures that transmitted data remains unaltered and authentic. Unlike simple checksums or hash values, HMAC requires a secret key, preventing unauthorized parties from generating valid authentication codes. This property makes HMAC essential for secure messaging protocols, financial transactions, and any scenario where data integrity and authenticity must be guaranteed.
        </p>
        <p className="text-gray-700 leading-relaxed">
          In cryptographic systems and token generation, HMAC provides secure building blocks for more complex authentication schemes. JSON Web Tokens (JWTs) use HMAC for signature verification, OAuth implementations use HMAC for request signing, and many secure cookie systems use HMAC for tamper detection. The algorithm's efficiency and proven security make it a preferred choice for performance-critical applications requiring strong authentication guarantees.
        </p>
      </div>

      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-4">How to Use This HMAC Generator?</h3>
        <p className="text-gray-700 leading-relaxed mb-4">
          Our HMAC generator is designed for security professionals and developers implementing authentication systems. Start by entering your secret key - this should be a strong, random string known only to authorized parties. The key length should match or exceed the hash function's output size for optimal security. For SHA-256, use keys of at least 32 bytes; for SHA-1, use keys of at least 20 bytes. Never use predictable keys or share them through insecure channels.
        </p>
        <p className="text-gray-700 leading-relaxed mb-4">
          Enter the message or data you want to authenticate in the message field. This can be any text content, JSON payload, API request parameters, or binary data represented as text. Choose your preferred hash function - SHA-256 is recommended for new applications due to its security and performance characteristics. The generator will compute the HMAC using the standard algorithm: HMAC(K, m) = H((K ⊕ opad) || H((K ⊕ ipad) || m)).
        </p>
        <p className="text-gray-700 leading-relaxed">
          Review the generated HMAC value and verify it matches your expected output. The tool displays the result in hexadecimal format for easy integration with most programming languages and systems. Copy the HMAC for use in your authentication headers, API signatures, or security tokens. Remember that both the sender and receiver must use the identical secret key and hash function to generate and verify the same HMAC value.
        </p>
      </div>

      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-4">Who Should Use This HMAC Generator?</h3>
        <p className="text-gray-700 leading-relaxed mb-6">
          API developers and backend engineers use our generator for implementing secure API authentication and request signing. When building REST APIs, microservices, or webhooks, developers need robust authentication mechanisms. The generator helps create HMAC signatures for API requests, verify webhook payloads, and implement secure communication between services without complex certificate management.
        </p>
        <p className="text-gray-700 leading-relaxed mb-6">
          Security architects and cryptography professionals rely on HMAC generators for designing secure authentication systems and protocols. When creating custom security solutions, implementing message authentication, or designing cryptographic protocols, professionals need reliable HMAC tools. The generator helps prototype authentication schemes, test HMAC implementations, and verify cryptographic operations across different platforms.
        </p>
        <p className="text-gray-700 leading-relaxed mb-6">
          DevOps engineers and system administrators use HMAC generators for securing infrastructure and automated systems. When configuring CI/CD pipelines, securing automated deployments, or implementing service-to-service authentication, administrators need strong authentication mechanisms. The generator helps create secure tokens, authenticate automated processes, and verify integrity of configuration files and deployment artifacts.
        </p>
        <p className="text-gray-700 leading-relaxed">
          Mobile app developers and IoT engineers use HMAC generators for securing client-server communication and device authentication. When developing mobile applications, IoT devices, or embedded systems, developers need efficient security solutions that work with limited resources. HMAC provides strong security with computational efficiency, making it ideal for resource-constrained environments requiring message authentication.
        </p>
      </div>

      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-4">HMAC Generation Examples and Applications</h3>
        <div className="bg-gray-50 p-6 rounded-lg mb-6">
          <h4 className="font-semibold text-gray-900 mb-3">Example 1: API Request Authentication</h4>
          <p className="text-gray-700 mb-3">
            Creating HMAC signature for API authentication:
          </p>
          <div className="code-block">
            <code className="text-sm text-gray-800">
              Secret Key: my-secret-api-key-12345<br />
              Message: GET/api/users?timestamp=1640995200<br />
              Hash Function: SHA-256<br />
              HMAC: a1b2c3d4e5f6... (64 characters)<br /><br />
              Use Case: REST API authentication
            </code>
          </div>
        </div>

        <div className="bg-gray-50 p-6 rounded-lg">
          <h4 className="font-semibold text-gray-900 mb-3">Example 2: Webhook Verification</h4>
          <p className="text-gray-700 mb-3">
            Verifying webhook payload integrity:
          </p>
          <div className="code-block">
            <code className="text-sm text-gray-800">
              Secret Key: webhook-secret-key<br />
              Message: &#123;"event":"payment","amount":100.00&#125;<br />
              Hash Function: SHA-256<br />
              HMAC: f7e8d9c0b1a2... (64 characters)<br /><br />
              Use Case: Webhook signature verification
            </code>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-4">HMAC Algorithm and Security Features</h3>
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-red-500 rounded-full flex-shrink-0 mt-1"></div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Cryptographic Security</h4>
              <p className="text-gray-700">
                HMAC provides strong security guarantees even if the underlying hash function has known weaknesses. The construction prevents length extension attacks and ensures that only parties with the secret key can generate valid authentication codes. The algorithm's security proofs and widespread adoption make it suitable for high-security applications.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-red-500 rounded-full flex-shrink-0 mt-1"></div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Key Management Security</h4>
              <p className="text-gray-700">
                HMAC security depends entirely on secret key protection. Keys must be generated using cryptographically secure random number generators, stored securely, and never transmitted over insecure channels. Key rotation and proper key lifecycle management are essential for maintaining long-term security in production systems.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-red-500 rounded-full flex-shrink-0 mt-1"></div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Hash Function Flexibility</h4>
              <p className="text-gray-700">
                HMAC works with any cryptographic hash function, allowing selection based on security requirements and performance needs. SHA-256 provides excellent security and performance for most applications, while SHA-512 offers higher security for sensitive data. The construction maintains security regardless of the chosen hash function.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-red-500 rounded-full flex-shrink-0 mt-1"></div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Performance Optimization</h4>
              <p className="text-gray-700">
                HMAC is computationally efficient compared to asymmetric cryptographic operations, making it suitable for high-throughput applications and resource-constrained environments. The algorithm's simplicity allows hardware acceleration and optimization while maintaining strong security guarantees for authentication and integrity verification.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-red-50 p-6 rounded-lg">
        <h4 className="font-semibold text-red-900 mb-3">HMAC Security Best Practices</h4>
        <p className="text-red-800 leading-relaxed">
          Always use cryptographically secure random keys of sufficient length. Never share HMAC keys through insecure channels or store them in client-side code. Implement proper key rotation policies and secure key storage using hardware security modules or key management systems. Use SHA-256 or stronger hash functions for new applications. Verify HMAC values before trusting message content and implement proper error handling for authentication failures.
        </p>
      </div>
    </div>
  ),

  faqs: [
    {
      question: 'What hash functions can be used with HMAC?',
      answer: 'HMAC works with any cryptographic hash function including SHA-256, SHA-512, SHA-1, and MD5. SHA-256 is recommended for new applications due to its security and performance. SHA-512 provides higher security for sensitive applications, while SHA-1 and MD5 should be avoided due to known vulnerabilities.'
    },
    {
      question: 'How secure is HMAC compared to digital signatures?',
      answer: 'HMAC is very secure but uses symmetric cryptography (shared secret key), while digital signatures use asymmetric cryptography (private/public key pairs). HMAC is more efficient and simpler to implement but requires secure key sharing. Digital signatures provide non-repudiation but are computationally more expensive.'
    },
    {
      question: 'What makes HMAC different from simple hash values?',
      answer: 'HMAC requires a secret key and prevents unauthorized parties from generating valid authentication codes. Simple hash values can be computed by anyone with the message, while HMAC can only be generated and verified by parties possessing the secret key, providing both authentication and integrity protection.'
    },
    {
      question: 'How long should HMAC secret keys be?',
      answer: 'Secret keys should be at least as long as the hash function output: 32 bytes for SHA-256, 64 bytes for SHA-512, 20 bytes for SHA-1. Keys longer than the hash output are hashed internally, so extremely long keys don\'t provide additional security. Use cryptographically secure random key generation.'
    },
    {
      question: 'Can HMAC be used for password hashing?',
      answer: 'No, HMAC is not suitable for password hashing because it\'s designed for fast computation. Password hashing should use slow, memory-hard functions like bcrypt, scrypt, or Argon2 with proper salting. HMAC is ideal for message authentication and API security, not password storage.'
    },
    {
      question: 'How is HMAC used in JWT and API authentication?',
      answer: 'JWT tokens use HMAC for signature verification, ensuring tokens haven\'t been tampered with. API authentication uses HMAC to sign requests with secret keys, allowing services to verify request authenticity. Both applications rely on shared secret keys between client and server for secure communication.'
    }
  ],

  relatedTools: [
    {
      name: 'Hash Generator',
      url: '/tools/hash-generator',
      description: 'Generate various hash values for cryptographic operations'
    },
    {
      name: 'JWT Decoder',
      url: '/tools/jwt-decoder',
      description: 'Decode and analyze JSON Web Tokens with HMAC signatures'
    },
    {
      name: 'Bcrypt Generator',
      url: '/tools/bcrypt-generator',
      description: 'Generate secure password hashes for authentication systems'
    },
    {
      name: 'SHA-256 Hash',
      url: '/tools/sha256-hash',
      description: 'Generate SHA-256 hashes for HMAC key generation'
    },
    {
      name: 'Base64 Encoder',
      url: '/tools/base64',
      description: 'Encode and decode data for secure transmission'
    }
  ]
} as const;
